import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { budgetApi, accountApi, clientApi } from '@/services/api'
import type { Account, Budget } from '@/types'
import { DollarSign, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/utils/currency'
import { Button } from '@/components/ui/button'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

type BudgetGrid = Record<string, Record<number, string>> // accountId → month → amount

export default function BudgetEntry() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [grid, setGrid] = useState<BudgetGrid>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [currency, setCurrency] = useState('USD')
  const [premiumGated, setPremiumGated] = useState(false)

  useEffect(() => {
    if (!clientId) return
    clientApi.get(clientId).then((r) => setCurrency(r.data.client.currency)).catch(() => {})
    setLoading(true)
    Promise.all([
      accountApi.list(clientId),
      budgetApi.list(clientId, year),
    ]).then(([accRes, budgetRes]) => {
      const accs: Account[] = accRes.data.accounts.filter(
        (a: Account) => (a.account_type === 'revenue' || a.account_type === 'expense') && !a.is_archived
      )
      setAccounts(accs)
      const g: BudgetGrid = {}
      for (const b of (budgetRes.data.budgets as Budget[])) {
        if (!g[b.account_id]) g[b.account_id] = {}
        g[b.account_id][b.month] = b.amount
      }
      setGrid(g)
    }).catch((err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 402) setPremiumGated(true)
      else toast.error('Failed to load budgets')
    })
      .finally(() => setLoading(false))
  }, [clientId, year])

  function setValue(accountId: string, month: number, val: string) {
    setGrid((g) => ({
      ...g,
      [accountId]: { ...(g[accountId] || {}), [month]: val },
    }))
  }

  async function handleSave() {
    if (!clientId) return
    setSaving(true)
    const items: Array<{ account_id: string; year: number; month: number; amount: string }> = []
    for (const [accountId, months] of Object.entries(grid)) {
      for (const [month, amount] of Object.entries(months)) {
        if (amount !== '' && !isNaN(Number(amount))) {
          items.push({ account_id: accountId, year, month: Number(month), amount })
        }
      }
    }
    try {
      await budgetApi.bulkUpsert({ client_ledger_id: clientId, items })
      toast.success('Budget saved')
    } catch {
      toast.error('Failed to save budget')
    } finally {
      setSaving(false)
    }
  }

  const revenueAccounts = accounts.filter((a) => a.account_type === 'revenue')
  const expenseAccounts = accounts.filter((a) => a.account_type === 'expense')

  function rowTotal(accountId: string) {
    const months = grid[accountId] || {}
    return Object.values(months).reduce((s, v) => s + (parseFloat(v) || 0), 0)
  }

  function renderSection(title: string, accs: Account[]) {
    if (accs.length === 0) return null
    return (
      <>
        <tr>
          <td colSpan={14} className="bg-surface-50 px-3 py-1.5 text-[13px] font-bold text-[rgba(0,10,30,0.75)] uppercase tracking-wider">
            {title}
          </td>
        </tr>
        {accs.map((acc) => (
          <tr key={acc.id} className="hover:bg-surface-50">
            <td className="px-3 py-1.5 text-[13px] text-[#000A1E] whitespace-nowrap sticky left-0 bg-white">
              <span className="text-[#CCCED2] mr-1">{acc.code}</span>{acc.name}
            </td>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <td key={m} className="px-1 py-1">
                <input
                  type="number"
                  step="0.01"
                  value={grid[acc.id]?.[m] ?? ''}
                  onChange={(e) => setValue(acc.id, m, e.target.value)}
                  className="w-20 text-[13px] border border-surface-200 rounded px-1.5 py-1 focus:outline-none focus:border-[#0078C8] text-right"
                  placeholder="0"
                />
              </td>
            ))}
            <td className="px-3 py-1.5 text-[13px] text-right font-medium text-[#000A1E]">
              {formatCurrency(rowTotal(acc.id), currency)}
            </td>
          </tr>
        ))}
      </>
    )
  }

  if (premiumGated) {
    return (
      <div className="max-w-sm mx-auto mt-16">
        <div className="card p-10 text-center">
          <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <DollarSign className="w-6 h-6 text-amber-500" />
          </div>
          <h2 className="text-[21px] font-bold text-[#000A1E] leading-[32px] mb-2">Premium Feature</h2>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-5">
            Budgeting requires a Premium client. Upgrade in Client Settings &rarr; Plan.
          </p>
          <Button variant="default" onClick={() => navigate(`/clients/${clientId}/settings`)}>
            Upgrade to Premium
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1">
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Budget Entry</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Monthly budget amounts by account</p>
        </div>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="input w-32"
        >
          {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <Button variant="default" onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Budget'}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-5 h-5 border-2 border-[#0078C8] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-surface-100">
                <th className="px-3 py-2.5 text-left text-[13px] font-bold text-[rgba(0,10,30,0.75)] sticky left-0 bg-white">Account</th>
                {MONTHS.map((m) => (
                  <th key={m} className="px-1 py-2.5 text-[13px] font-bold text-[rgba(0,10,30,0.75)] text-center min-w-[88px]">{m}</th>
                ))}
                <th className="px-3 py-2.5 text-[13px] font-bold text-[rgba(0,10,30,0.75)] text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-50">
              {renderSection('Revenue', revenueAccounts)}
              {renderSection('Expenses', expenseAccounts)}
            </tbody>
          </table>
          {accounts.length === 0 && (
            <p className="text-center py-8 text-[13px] text-[#CCCED2]">No revenue or expense accounts found</p>
          )}
        </div>
      )}
    </div>
  )
}
